import React, { useState, useEffect } from 'react'
import {
  Container,
  Grid,
  Header,
  Segment,
  Card,
  Icon,
  Button,
  Dimmer,
  Loader,
  Message,
  Input,
  Dropdown,
  Label,
  Tab,
} from 'semantic-ui-react'
import { Link } from 'react-router-dom'
import { fetchResources } from '../../../lib/datocms'
import './Resources.css'

const Resources = () => {
  const [resources, setResources] = useState([])
  const [filteredResources, setFilteredResources] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedType, setSelectedType] = useState('all')

  useEffect(() => {
    loadResources()
  }, [])

  useEffect(() => {
    filterResources()
  }, [resources, searchTerm, selectedCategory, selectedType])

  const loadResources = async () => {
    try {
      setLoading(true)
      const data = await fetchResources()
      setResources(data)
      setFilteredResources(data)
    } catch (err) {
      console.error('Error loading resources:', err)
      setError('Failed to load resources. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  const filterResources = () => {
    let filtered = [...resources]

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(resource =>
        resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.description?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(resource => resource.category === selectedCategory)
    }

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(resource => resource.type === selectedType)
    }

    setFilteredResources(filtered)
  }

  const categoryOptions = [
    { key: 'all', value: 'all', text: 'All Categories' },
    { key: 'guides', value: 'guides', text: 'Guides' },
    { key: 'whitepapers', value: 'whitepapers', text: 'Whitepapers' },
    { key: 'casestudies', value: 'casestudies', text: 'Case Studies' },
    { key: 'webinars', value: 'webinars', text: 'Webinars' },
    { key: 'blog', value: 'blog', text: 'Blog Posts' },
  ]

  const typeOptions = [
    { key: 'all', value: 'all', text: 'All Types' },
    { key: 'pdf', value: 'pdf', text: 'PDF' },
    { key: 'video', value: 'video', text: 'Video' },
    { key: 'article', value: 'article', text: 'Article' },
    { key: 'tool', value: 'tool', text: 'Tool' },
  ]

  const getResourceIcon = (type) => {
    switch (type) {
      case 'pdf': return 'file pdf'
      case 'video': return 'video'
      case 'article': return 'newspaper'
      case 'tool': return 'wrench'
      default: return 'file'
    }
  }

  const renderResourceCard = (resource) => {
    return (
      <Card key={resource.id} className="resource-card">
        {resource.thumbnail?.url && (
          <img 
            src={resource.thumbnail.url} 
            alt={resource.thumbnail.alt || resource.title}
            className="resource-thumbnail"
          />
        )}
        <Card.Content>
          <Label corner='right'>
            <Icon name={getResourceIcon(resource.type)} />
          </Label>
          {resource.featured && (
            <Label color='yellow' ribbon>
              Featured
            </Label>
          )}
          <Card.Header>{resource.title}</Card.Header>
          <Card.Meta>
            <span className='date'>{resource.publishedDate}</span>
            {resource.author && <span> • {resource.author}</span>}
          </Card.Meta>
          <Card.Description>{resource.excerpt || resource.description}</Card.Description>
          {resource.tags && resource.tags.length > 0 && (
            <div className="resource-tags">
              {resource.tags.map((tag, index) => (
                <Label key={index} size='small'>{tag}</Label>
              ))}
            </div>
          )}
        </Card.Content>
        <Card.Content extra>
          {resource.downloadUrl ? (
            <Button 
              primary 
              fluid 
              as='a' 
              href={resource.downloadUrl} 
              target='_blank'
            >
              <Icon name='download' /> Download
            </Button>
          ) : resource.videoUrl ? (
            <Button 
              primary 
              fluid 
              as='a' 
              href={resource.videoUrl} 
              target='_blank'
            >
              <Icon name='play' /> Watch
            </Button>
          ) : (
            <Button primary fluid as={Link} to={`/resources/${resource.slug}`}>
              <Icon name='eye' /> View Resource
            </Button>
          )}
        </Card.Content>
      </Card>
    )
  }

  const featuredResources = filteredResources.filter(r => r.featured)
  const regularResources = filteredResources.filter(r => !r.featured)

  const panes = [
    {
      menuItem: 'Getting Started',
      render: () => (
        <Tab.Pane>
          <Grid columns={3} stackable>
            <Grid.Column>
              <Card>
                <Card.Content>
                  <Icon name='rocket' size='huge' color='blue' />
                  <Card.Header>Quick Start Guide</Card.Header>
                  <Card.Description>
                    Get up and running with Trading Systems in under 10 minutes
                  </Card.Description>
                </Card.Content>
                <Card.Content extra>
                  <Button primary fluid>Download Guide</Button>
                </Card.Content>
              </Card>
            </Grid.Column>
            <Grid.Column>
              <Card>
                <Card.Content>
                  <Icon name='video' size='huge' color='green' />
                  <Card.Header>Video Tutorials</Card.Header>
                  <Card.Description>
                    Step-by-step video guides for all platform features
                  </Card.Description>
                </Card.Content>
                <Card.Content extra>
                  <Button primary fluid>Watch Videos</Button>
                </Card.Content>
              </Card>
            </Grid.Column>
            <Grid.Column>
              <Card>
                <Card.Content>
                  <Icon name='book' size='huge' color='orange' />
                  <Card.Header>Documentation</Card.Header>
                  <Card.Description>
                    Comprehensive documentation and API references
                  </Card.Description>
                </Card.Content>
                <Card.Content extra>
                  <Button primary fluid>Browse Docs</Button>
                </Card.Content>
              </Card>
            </Grid.Column>
          </Grid>
        </Tab.Pane>
      ),
    },
    {
      menuItem: 'Best Practices',
      render: () => (
        <Tab.Pane>
          <Grid columns={2} stackable>
            <Grid.Column>
              <Header as='h3'>Industry Best Practices</Header>
              <p>Learn from industry leaders and optimize your operations</p>
              <Button primary>Explore Best Practices</Button>
            </Grid.Column>
            <Grid.Column>
              <Segment placeholder>
                <Header icon>
                  <Icon name='lightbulb' />
                  Best Practices Resources
                </Header>
              </Segment>
            </Grid.Column>
          </Grid>
        </Tab.Pane>
      ),
    },
    {
      menuItem: 'Support',
      render: () => (
        <Tab.Pane>
          <Grid columns={2} stackable>
            <Grid.Column>
              <Header as='h3'>Get Help When You Need It</Header>
              <p>24/7 support, community forums, and expert assistance</p>
              <Button primary>Contact Support</Button>
            </Grid.Column>
            <Grid.Column>
              <Segment placeholder>
                <Header icon>
                  <Icon name='help circle' />
                  Support Resources
                </Header>
              </Segment>
            </Grid.Column>
          </Grid>
        </Tab.Pane>
      ),
    },
  ]

  if (loading) {
    return (
      <Dimmer active inverted>
        <Loader size='large'>Loading Resources...</Loader>
      </Dimmer>
    )
  }

  if (error) {
    return (
      <Container style={{ marginTop: '2em' }}>
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      </Container>
    )
  }

  return (
    <div className="resources-page">
      {/* Hero Section */}
      <Segment className="resources-hero" vertical>
        <Container text textAlign='center'>
          <Header as='h1' inverted>
            Resources & Learning Center
          </Header>
          <p className="hero-subtitle">
            Everything you need to succeed with Trading Systems. From getting started 
            guides to advanced strategies, find the resources to grow your business.
          </p>
          <Input
            size='large'
            icon='search'
            placeholder='Search resources...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="resource-search"
          />
        </Container>
      </Segment>

      {/* Quick Access Tabs */}
      <Segment className="quick-access" vertical>
        <Container>
          <Header as='h2' textAlign='center' style={{ marginBottom: '2em' }}>
            Quick Access
          </Header>
          <Tab panes={panes} />
        </Container>
      </Segment>

      {/* Resource Filters */}
      <Segment className="resource-filters" vertical>
        <Container>
          <Grid columns={3} stackable>
            <Grid.Column width={6}>
              <Input
                fluid
                icon='search'
                placeholder='Search resources...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Grid.Column>
            <Grid.Column width={5}>
              <Dropdown
                fluid
                selection
                options={categoryOptions}
                value={selectedCategory}
                onChange={(e, { value }) => setSelectedCategory(value)}
                placeholder='Select Category'
              />
            </Grid.Column>
            <Grid.Column width={5}>
              <Dropdown
                fluid
                selection
                options={typeOptions}
                value={selectedType}
                onChange={(e, { value }) => setSelectedType(value)}
                placeholder='Select Type'
              />
            </Grid.Column>
          </Grid>
        </Container>
      </Segment>

      {/* Featured Resources */}
      {featuredResources.length > 0 && (
        <Segment className="featured-resources" vertical>
          <Container>
            <Header as='h2' textAlign='center' style={{ marginBottom: '2em' }}>
              Featured Resources
            </Header>
            <Card.Group itemsPerRow={3} stackable>
              {featuredResources.map(resource => renderResourceCard(resource))}
            </Card.Group>
          </Container>
        </Segment>
      )}

      {/* All Resources Grid from Dato CMS */}
      <Segment className="resources-grid" vertical>
        <Container>
          <Header as='h2' textAlign='center' style={{ marginBottom: '2em' }}>
            {selectedCategory === 'all' && selectedType === 'all' 
              ? 'All Resources' 
              : 'Filtered Resources'}
            <Label circular>{filteredResources.length}</Label>
          </Header>
          {regularResources.length > 0 ? (
            <Card.Group itemsPerRow={3} stackable>
              {regularResources.map(resource => renderResourceCard(resource))}
            </Card.Group>
          ) : (
            <Message info>
              <Message.Header>No resources found</Message.Header>
              <p>Try adjusting your filters or search terms.</p>
            </Message>
          )}
        </Container>
      </Segment>

      {/* Newsletter Signup */}
      <Segment className="newsletter-signup" vertical>
        <Container textAlign='center'>
          <Header as='h2'>Stay Updated</Header>
          <p style={{ fontSize: '1.1em', marginBottom: '2em' }}>
            Get the latest resources, tips, and industry insights delivered to your inbox.
          </p>
          <Grid>
            <Grid.Column computer={8} mobile={16} floated='right'>
              <Input
                fluid
                action={{
                  color: 'blue',
                  content: 'Subscribe',
                  icon: 'mail',
                }}
                placeholder='Enter your email...'
                size='large'
              />
            </Grid.Column>
          </Grid>
        </Container>
      </Segment>

      {/* CTA Section */}
      <Segment className="resources-cta" inverted vertical>
        <Container textAlign='center'>
          <Header as='h2' inverted>
            Need Personalized Help?
          </Header>
          <p style={{ fontSize: '1.1em', marginBottom: '2em' }}>
            Our experts are ready to help you get the most out of Trading Systems.
          </p>
          <Button size='large' inverted as={Link} to='/contact-us'>
            Talk to an Expert
          </Button>
          <Button size='large' inverted basic as='a' href='#'>
            Join Community
          </Button>
        </Container>
      </Segment>
    </div>
  )
}

export default Resources